import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, BookOpen, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, egp, mediaUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { FullLoader } from "@/components/site/Layout";
import { Progress } from "@/components/ui/progress";

const STATUS = {
  pending: ["قيد المراجعة", "bg-purple-100 text-brand", Clock],
  approved: ["تم التفعيل", "bg-emerald-50 text-emerald-700", CheckCircle2],
  rejected: ["مرفوض", "bg-red-50 text-red-600", XCircle],
};
const METHOD = { vodafone_cash: "فودافون كاش", instapay: "إنستاباي" };

const ResumeCard = ({ item }) => (
  <div className="rounded-3xl bg-white border border-line p-6 sm:p-8 flex flex-col md:flex-row gap-6 items-start md:items-center" data-testid="resume-card">
    {item.course.cover_image && <img src={mediaUrl(item.course.cover_image)} alt="" className="w-full md:w-56 aspect-video object-cover rounded-2xl" />}
    <div className="flex-1 space-y-3 w-full">
      <p className="text-xs text-[var(--kh-violet-2)] tracking-wide">كمّل من مكان ما وقفت</p>
      <h2 className="font-heading text-2xl font-bold text-ink">{item.course.title}</h2>
      {item.last_lesson_title && <p className="text-sm text-soft">آخر درس: {item.last_lesson_title}</p>}
      <div className="flex items-center gap-3"><Progress value={item.percent} className="h-2 bg-purple-100" /><span className="text-sm text-soft w-12">{item.percent}%</span></div>
    </div>
    <Link to={`/learn/${item.course.id}${item.last_lesson_id ? `/${item.last_lesson_id}` : ""}`} data-testid="resume-learning-btn"
      className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FFFFFF] text-[var(--kh-violet)] hover:bg-[#F3E8FF] font-semibold transition-colors">
      <PlayCircle size={20} />متابعة التعلم
    </Link>
  </div>
);

export default function Dashboard() {
  useSeo("لوحة الطالب");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <FullLoader />;
  const resume = d.courses[0];
  return (
    <div className="bg-soft min-h-screen" data-testid="student-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-12">
        <header>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink" data-testid="dashboard-greeting">أهلاً، {d.user.name}</h1>
          <p className="text-soft mt-1">ده مكانك الهادي للتعلم.</p>
        </header>
        <div className="grid grid-cols-3 gap-4">
          {[["كورساتي", d.courses.length, "stat-courses"], ["دروس مكتملة", d.courses.reduce((a, c) => a + c.completed, 0), "stat-completed"], ["طلبات قيد المراجعة", d.payments.filter((p) => p.status === "pending").length, "stat-pending"]].map(([l, v, id]) => (
            <div key={id} className="rounded-2xl bg-white border border-line p-5">
              <p className="text-xs sm:text-sm text-soft">{l}</p>
              <p className="font-display text-3xl text-ink mt-1" data-testid={id}>{v}</p>
            </div>
          ))}
        </div>
        {resume && <ResumeCard item={resume} />}
        <section>
          <h2 className="font-heading text-xl font-bold text-ink mb-5">كورساتي</h2>
          {d.courses.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#C9A3EE] p-10 text-center space-y-3" data-testid="dashboard-no-courses">
              <BookOpen className="mx-auto text-soft" />
              <p className="text-soft">لسه مفيش كورسات مفعّلة في حسابك.</p>
              <Link to="/courses" className="inline-block text-[var(--kh-violet-2)] hover:underline">تصفح الكورسات</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {d.courses.map((c) => (
                <Link key={c.course.id} to={`/learn/${c.course.id}${c.last_lesson_id ? `/${c.last_lesson_id}` : ""}`} data-testid={`my-course-${c.course.id}`}
                  className="rounded-2xl bg-white border border-line hover:border-[var(--kh-violet)]/50 overflow-hidden transition-colors">
                  {c.course.cover_image && <img src={mediaUrl(c.course.cover_image)} alt="" className="w-full aspect-video object-cover" />}
                  <div className="p-5 space-y-3">
                    <h3 className="font-semibold text-ink leading-snug">{c.course.title}</h3>
                    <Progress value={c.percent} className="h-1.5 bg-purple-100" />
                    <p className="text-xs text-soft">{c.completed} من {c.total_lessons} درس • {c.percent}%</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
        <section>
          <h2 className="font-heading text-xl font-bold text-ink mb-5">طلبات الدفع</h2>
          {d.payments.length === 0 ? <p className="text-soft text-sm">مفيش طلبات دفع.</p> : (
            <div className="space-y-3" data-testid="dashboard-payments">
              {d.payments.map((p) => {
                const [label, cls, Icon] = STATUS[p.status];
                return (
                  <div key={p.id} className="rounded-2xl bg-white border border-line p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`payment-row-${p.id}`}>
                    <div>
                      <p className="text-ink font-medium">{p.course_title}</p>
                      <p className="text-xs text-soft">{METHOD[p.method]} • {egp(p.amount)} • {new Date(p.created_at).toLocaleDateString("ar-EG")}</p>
                      {p.status === "rejected" && p.admin_note && <p className="text-xs text-red-600 mt-1">سبب الرفض: {p.admin_note}</p>}
                    </div>
                    <span className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full ${cls}`} data-testid={`payment-status-${p.id}`}><Icon size={14} />{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
